import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Edit, Loader2 } from 'lucide-react';
import CurrentClientContext from '../../providers/CurrentClientProvider';
import AuthContext from '../../providers/AuthProvider';
import constants from '../../constants/constant';
import {
    buildClientReportsLibraryPath,
    isV3ClientDashboardPath,
} from '../../services/ClientDashboardV3';

export default function EditCustomReport() {
    const { clientId, client_code, filterId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const client_context = CurrentClientContext.useClient();
    const auth_context = AuthContext.useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const isDashboardV3View = isV3ClientDashboardPath(location.pathname);

    const activeClientId = client_context.client?.Id || clientId;
    const clientName = client_context.client?.Name || '';

    // Get filter name from location state if available
    const filterName = location.state?.filterName || 'Custom Report';

    useEffect(() => {
        document.title = 'Retail ORBIT: Edit Custom Report';
    }, []);

    // Wait for client data to be loaded before showing the iframe
    useEffect(() => {
        if (client_context.client && client_context.client.Name && auth_context.user) {
            setIsLoading(false);
        }
    }, [client_context.client, auth_context.user]);

    const handleBack = () => {
        navigate(buildClientReportsLibraryPath({
            clientCode: client_code,
            clientId: activeClientId,
            isV3: isDashboardV3View,
        }), {
            state: { tab: 'custom' }
        });
    };

    const source = 'bq';
    const is_dev = process.env.NODE_ENV === 'development' ? 1 : 0;
    const filterFormUrl = `${constants.BACKEND_DOMAIN}/filters-form/${filterId}?user_id=${auth_context.user?.id}&client_code=${activeClientId}&client_name=${encodeURIComponent(clientName)}&is_dev=${is_dev}&source=${source}`;

    // Listen for messages from the iframe to detect when form is submitted
    useEffect(() => {
        const handleMessage = (event) => {
            // Check if message is from our iframe
            if (event.origin === constants.BACKEND_DOMAIN) {
                if (event.data.type === 'FILTER_SAVED') {
                    // Redirect back to new reports library after successful save
                    setTimeout(() => {
                        navigate(buildClientReportsLibraryPath({
                            clientCode: client_code,
                            clientId: activeClientId,
                            isV3: isDashboardV3View,
                        }), {
                            state: { tab: 'custom', message: 'Custom report updated successfully!' }
                        });
                    }, 1000);
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [navigate, client_code, activeClientId, isDashboardV3View]);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={handleBack}
                                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span className="font-medium">Back to Reports</span>
                            </button>
                            <div className="h-6 w-px bg-gray-300" />
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-purple-50 border rounded-lg flex items-center justify-center">
                                    <Edit className="w-5 h-5 text-purple-700" />
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900">Edit Custom Report</h1>
                                    <p className="text-xs text-gray-500">{filterName}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Embedded Form or Loading */}
            <div className="max-w-7xl mx-auto px-8 py-6">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-12" style={{ minHeight: 'calc(100vh - 200px)' }}>
                            <Loader2 className="w-8 h-8 animate-spin text-purple-700 mb-4" />
                            <p className="text-gray-600">Loading client data...</p>
                        </div>
                    ) : (
                        <iframe
                            src={filterFormUrl}
                            className="w-full border-0"
                            style={{ minHeight: 'calc(100vh - 200px)', height: '100%' }}
                            title="Edit Custom Report Form"
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

